import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { IDigitalProductBillingType } from '../../models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../models/digitalProduct.model';
import { DigitalProductService } from '../../services/digitalProduct.service';
import { digitalProductActions } from '../../store/digitalProduct.actions';
import { getDigitalProductsSelector } from '../../store/digitalProduct.selectors';

@Component({
    selector: 'clp-digital-product-list',
    styleUrls: ['./digitalProductList.component.scss'],
    templateUrl: './digitalProductList.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ToolbarComponent
    ],
    providers: [
        DigitalProductService,
        ToastrService
    ]
})
export class DigitalProductListComponent implements OnInit {
    private digitalProductService = inject(DigitalProductService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private store = inject(Store);

    digitalProducts$!: Observable<IDigitalProduct[]>;
    searchTerm = '';
    billingTypeFilter: 'all' | '1' = 'all';
    visibilityFilter: 'all' | '1' | '2' | '3' = 'all';
    digitalProductToDelete: IDigitalProduct | null = null;
    deleting = false;
    private readonly billingTypeMap: Record<string, IDigitalProductBillingType> = {
        '1': IDigitalProductBillingType.OneTime,
        'OneTime': IDigitalProductBillingType.OneTime,
        '2': IDigitalProductBillingType.Recurring,
        'Recurring': IDigitalProductBillingType.Recurring
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };

    ngOnInit(): void {
        this.store.dispatch(digitalProductActions.getDigitalProducts());
        this.digitalProducts$ = this.store.select(getDigitalProductsSelector);
    }

    editDigitalProduct(id: string | undefined): void {
        this.router.navigate(['updateDigitalProduct', id]);
    }

    openDeleteConfirmation(digitalProduct: IDigitalProduct): void {
        if (!digitalProduct.id) {
            return;
        }

        this.digitalProductToDelete = digitalProduct;
    }

    closeDeleteConfirmation(): void {
        if (this.deleting) {
            return;
        }

        this.digitalProductToDelete = null;
    }

    confirmDeleteDigitalProduct(): void {
        const id = this.digitalProductToDelete?.id;
        if (!id) {
            this.closeDeleteConfirmation();
            return;
        }

        this.deleting = true;
        this.digitalProductService.deleteDigitalProduct(id).subscribe({
            next: () => {
                this.deleting = false;
                this.digitalProductToDelete = null;
                this.toastService.show(this.translate.instant('digitalProduct.delete.successMessage'), '');
                this.store.dispatch(digitalProductActions.getDigitalProducts());
            },
            error: () => {
                this.deleting = false;
            }
        });
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeDeleteConfirmation();
    }

    getCountByVisibility(digitalProducts: IDigitalProduct[], visibility: IDigitalProductVisibility): number {
        return digitalProducts.filter(digitalProduct => this.resolveVisibility(digitalProduct.visibility) === visibility).length;
    }

    onSearchChange(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
    }

    onBillingTypeFilterChange(event: Event): void {
        this.billingTypeFilter = (event.target as HTMLSelectElement).value as 'all' | '1';
    }

    onVisibilityFilterChange(event: Event): void {
        this.visibilityFilter = (event.target as HTMLSelectElement).value as 'all' | '1' | '2' | '3';
    }

    getFilteredDigitalProducts(digitalProducts: IDigitalProduct[]): IDigitalProduct[] {
        const search = this.searchTerm.trim().toLowerCase();
        const selectedBillingType = this.billingTypeFilter;
        const selectedVisibility = this.visibilityFilter;
        const selectedBillingTypeValue = selectedBillingType === 'all'
            ? null
            : this.resolveBillingType(selectedBillingType);
        const selectedVisibilityValue = selectedVisibility === 'all'
            ? null
            : this.resolveVisibility(selectedVisibility);

        return digitalProducts.filter(digitalProduct => {
            const name = (digitalProduct.name ?? '').toLowerCase();
            const description = (digitalProduct.description ?? '').toLowerCase();
            const currency = (digitalProduct.currency ?? '').toLowerCase();
            const matchesSearch = !search || name.includes(search) || description.includes(search) || currency.includes(search);
            const matchesBillingType = selectedBillingTypeValue === null
                || this.resolveBillingType(digitalProduct.billingType) === selectedBillingTypeValue;
            const matchesVisibility = selectedVisibilityValue === null
                || this.resolveVisibility(digitalProduct.visibility) === selectedVisibilityValue;

            return matchesSearch && matchesBillingType && matchesVisibility;
        });
    }

    getVisibilityBadgeClass(visibility: unknown): string {
        switch (this.resolveVisibility(visibility)) {
            case 2:
                return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';
            case 3:
                return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
            case 1:
            default:
                return 'border-slate-600 bg-slate-700/50 text-slate-200';
        }
    }

    getBillingTypeBadgeClass(billingType: unknown): string {
        switch (this.resolveBillingType(billingType)) {
            case IDigitalProductBillingType.Recurring:
                return 'border-sky-500/40 bg-sky-500/15 text-sky-300';
            case IDigitalProductBillingType.OneTime:
            default:
                return 'border-violet-500/40 bg-violet-500/15 text-violet-300';
        }
    }

    isRecurring(billingType: unknown): boolean {
        return this.resolveBillingType(billingType) === IDigitalProductBillingType.Recurring;
    }

    getBillingTypeLabelKey(billingType: unknown): string {
        return `digitalProduct.billingType.${this.resolveBillingType(billingType) ?? IDigitalProductBillingType.OneTime}`;
    }

    getVisibilityLabelKey(visibility: unknown): string {
        return `digitalProduct.visibility.${this.resolveVisibility(visibility) ?? IDigitalProductVisibility.Draft}`;
    }

    getRecurringIntervalLabelKey(recurringInterval: unknown): string {
        return `digitalProduct.recurringInterval.${this.resolveRecurringInterval(recurringInterval) ?? IDigitalProductRecurringInterval.None}`;
    }

    private resolveBillingType(value: unknown): IDigitalProductBillingType | undefined {
        return this.resolveEnumValue(value, this.billingTypeMap);
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility | undefined {
        return this.resolveEnumValue(value, this.visibilityMap);
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval | undefined {
        return this.resolveEnumValue(value, this.recurringIntervalMap);
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        const normalized = String(value).trim();
        return map[normalized];
    }
}
